/**
 * Activity Log Types
 *
 * Types for the Activity Log feature matching the backend API structure
 * from galaxy-server PR #862
 */

/**
 * Activity action types enum
 * Format: category.action (e.g., team.create, auth.login)
 */
export enum ActivityActionType {
  // Team actions
  teamCreate = 'team.create',
  teamUpdate = 'team.update',
  teamReactivate = 'team.reactivate',
  teamArchive = 'team.archive',
  teamApproveDeletion = 'team.approve_deletion',
  teamRevertDeletionApproval = 'team.revert_deletion_approval',
  teamGenerateApiKey = 'team.generate_api_key',

  // Member actions
  memberUpdate = 'member.update',
  memberResetPassword = 'member.reset_password',
  memberExportSandboxCsv = 'member.export_sandbox_csv',
  memberBatchDeleteTimeEntries = 'member.batch_delete_time_entries',

  // Tenant actions
  tenantCreate = 'tenant.create',
  tenantUpdate = 'tenant.update',
  tenantArchive = 'tenant.archive',
  tenantCreateDbCredential = 'tenant.create_db_credential',
  tenantRegisterService = 'tenant.register_service',

  // Realm actions
  realmCreate = 'realm.create',
  realmDestroy = 'realm.destroy',
  realmUpdateSandbox = 'realm.update_sandbox',
  realmInitializeSandbox = 'realm.initialize_sandbox',
  realmInitializeSubscription = 'realm.initialize_subscription',
  realmAddService = 'realm.add_service',
  realmInitAggregateInfra = 'realm.init_aggregate_infra',
  realmCleanupAggregateInfra = 'realm.cleanup_aggregate_infra',
  realmCreateDbDump = 'realm.create_db_dump',
  realmCreateSingleDbDump = 'realm.create_single_db_dump',

  // Integration actions
  integrationProvision = 'integration.provision',
  integrationArchive = 'integration.archive',
  integrationRestore = 'integration.restore',
  integrationConfigureV2 = 'integration.configure_v2',
  integrationUpdateNotes = 'integration.update_notes',
  integrationForceUpdateMapping = 'integration.force_update_mapping',
  integrationManualFetchData = 'integration.manual_fetch_data',
  integrationProvisionBak = 'integration.provision_bak',

  // Auth actions
  authLogin = 'auth.login',
  authImpersonate = 'auth.impersonate',
  authSetTempPassword = 'auth.set_temp_password',
  authBulkActivateAccounts = 'auth.bulk_activate_accounts',

  // Report actions
  reportGenerateDeletedWorkPlanExport = 'report.generate_deleted_work_plan_export',
  reportGenerateDemandReport = 'report.generate_demand_report',
  // Retained for historical activity log rows; the triggering UI was removed.
  reportRecalculateBudgetAggregates = 'report.recalculate_budget_aggregates',

  // CSM actions
  csmCreateAssignment = 'csm.create_assignment',
}

/**
 * Human-readable labels for action types
 */
export const ActivityActionTypeLabels: Record<ActivityActionType, string> = {
  [ActivityActionType.teamCreate]: 'Team Create',
  [ActivityActionType.teamUpdate]: 'Team Update',
  [ActivityActionType.teamReactivate]: 'Team Reactivate',
  [ActivityActionType.teamArchive]: 'Team Archive',
  [ActivityActionType.teamApproveDeletion]: 'Team Approve Deletion',
  [ActivityActionType.teamRevertDeletionApproval]: 'Team Revert Deletion Approval',
  [ActivityActionType.teamGenerateApiKey]: 'Team Generate API Key',

  [ActivityActionType.memberUpdate]: 'Member Update',
  [ActivityActionType.memberResetPassword]: 'Member Reset Password',
  [ActivityActionType.memberExportSandboxCsv]: 'Member Export Sandbox CSV',
  [ActivityActionType.memberBatchDeleteTimeEntries]: 'Member Batch Delete Time Entries',

  [ActivityActionType.tenantCreate]: 'Tenant Create',
  [ActivityActionType.tenantUpdate]: 'Tenant Update',
  [ActivityActionType.tenantArchive]: 'Tenant Archive',
  [ActivityActionType.tenantCreateDbCredential]: 'Tenant Create DB Credential',
  [ActivityActionType.tenantRegisterService]: 'Tenant Register Service',

  [ActivityActionType.realmCreate]: 'Realm Create',
  [ActivityActionType.realmDestroy]: 'Realm Destroy',
  [ActivityActionType.realmUpdateSandbox]: 'Realm Update Sandbox',
  [ActivityActionType.realmInitializeSandbox]: 'Realm Initialize Sandbox',
  [ActivityActionType.realmInitializeSubscription]: 'Realm Initialize Subscription',
  [ActivityActionType.realmAddService]: 'Realm Add Service',
  [ActivityActionType.realmInitAggregateInfra]: 'Realm Init Aggregate Infra',
  [ActivityActionType.realmCleanupAggregateInfra]: 'Realm Cleanup Aggregate Infra',
  [ActivityActionType.realmCreateDbDump]: 'Realm Create DB Dump',
  [ActivityActionType.realmCreateSingleDbDump]: 'Realm Create Single DB Dump',

  [ActivityActionType.integrationProvision]: 'Integration Provision',
  [ActivityActionType.integrationArchive]: 'Integration Archive',
  [ActivityActionType.integrationRestore]: 'Integration Restore',
  [ActivityActionType.integrationConfigureV2]: 'Integration Configure V2',
  [ActivityActionType.integrationUpdateNotes]: 'Integration Update Notes',
  [ActivityActionType.integrationForceUpdateMapping]: 'Integration Force Update Mapping',
  [ActivityActionType.integrationManualFetchData]: 'Integration Manual Fetch Data',
  [ActivityActionType.integrationProvisionBak]: 'Integration Provision BAK',

  [ActivityActionType.authLogin]: 'Auth Login',
  [ActivityActionType.authImpersonate]: 'Auth Impersonate',
  [ActivityActionType.authSetTempPassword]: 'Auth Set Temp Password',
  [ActivityActionType.authBulkActivateAccounts]: 'Auth Bulk Activate Accounts',

  [ActivityActionType.reportGenerateDeletedWorkPlanExport]:
    'Report Generate Deleted Work Plan Export',
  [ActivityActionType.reportGenerateDemandReport]: 'Report Generate Demand Report',
  [ActivityActionType.reportRecalculateBudgetAggregates]: 'Report Recalculate Budget Aggregates',

  [ActivityActionType.csmCreateAssignment]: 'CSM Create Assignment',
};

/**
 * Source of an activity log entry - indicates whether the action originated
 * via an MCP tool call, a direct API request, or the Galaxy UI.
 */
export type ActivityLogSource = 'mcp' | 'api' | 'ui';

/**
 * Human-readable labels for activity log sources
 */
export const ActivityLogSourceLabels: Record<ActivityLogSource, string> = {
  mcp: 'via MCP',
  api: 'API',
  ui: 'UI',
};

/**
 * Sort fields for activity log queries
 */
export enum ActivityLogSortBy {
  occurredAt = 'occurredAt',
  actionType = 'actionType',
  username = 'username',
  tenant = 'tenant',
  realmId = 'realmId',
}

/**
 * Sort order enum
 */
export enum SortOrder {
  ASC = 'ASC',
  DESC = 'DESC',
}

/**
 * Details JSONB stored on an activity log row.
 *
 * Rows created by the galaxy-server ActivityLogInterceptor snapshot the
 * request ({ method, path, params?, query?, body? }); rows enriched by
 * downstream services may instead carry before/after/metadata.
 */
export interface ActivityLogDetails {
  method?: string;
  path?: string;
  params?: Record<string, unknown>;
  query?: Record<string, unknown>;
  body?: Record<string, unknown>;
  before?: Record<string, unknown>;
  after?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

/**
 * Activity log item from backend API
 */
export interface ActivityLogItem {
  id: string;
  occurredAt: Date | string;
  username?: string;
  email?: string;
  accessLevel?: string;
  tenant?: string;
  realmId?: string;
  teamId?: string;
  actionType: string;
  realmType?: string;
  /** Origin of the action: 'mcp' | 'api' | 'ui'. Null/undefined for older rows predating this field. */
  source?: ActivityLogSource | null;
  summary?: string;
  details?: ActivityLogDetails;
  networkLogId?: number;
  actionLogId?: number;
}

/**
 * Pagination metadata from backend API
 */
export interface ActivityLogPagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

/**
 * Activity log API response
 */
export interface ActivityLogResponse {
  data: ActivityLogItem[];
  pagination: ActivityLogPagination;
}

/**
 * Query parameters for fetching activity logs
 *
 * @see galaxy-server PR #872 for backend API updates
 */
export interface ActivityLogQueryParams {
  // Filters
  user?: string; // Mapped to userSearch in backend (ILIKE on username/email)
  actionType?: string; // Comma-separated action types
  source?: ActivityLogSource; // Filter by origin: 'mcp' | 'api' | 'ui'
  tenant?: string;
  realmId?: string;
  teamId?: string; // Accepts comma-separated team IDs
  startDate?: string; // ISO 8601 format
  endDate?: string; // ISO 8601 format

  // Pagination
  page?: number;
  limit?: number;

  // Sorting
  sortBy?: ActivityLogSortBy;
  sortOrder?: SortOrder;
}
